import InteractionEvent = PIXI.interaction.InteractionEvent;
import { PIXILoader } from "../../loading/PIXILoader";
import { MobileView } from "../../MobileView";
import { Device } from "../../Device";
import { Loader } from "../../loading/Loader";

export interface IScrollElementFactory<T> {
    instance(): IScrollElement<T>;
}

export interface IScrollConfig {
    portrait: {
        scrollConfig: IScrollElementConfig;
    };
    landscape: {
        scrollConfig: IScrollElementConfig;
    };
}

export interface IScrollElement<T> extends PIXI.Container {
    setData(data: T): void;
    updatePositions(): void;
}

export interface IScrollElementConfig {
    maskSize: {width: number, height: number}
    elementsVerticalOffset: number;
    placeholderTextureID?: string;
    fadeConfig?: {fadeTextureId: string, isReverse?: boolean}
    scrollBarConfig?: IScrollBarConfig;
}

export interface IScrollBarConfig {
    width: number;
    radius: number;
    rightOffset: number;
    trackFill: number;
    thumbFill: number;
    thumbSize: number;
    minThumbSize: number;
}

export class RecyclerScrollView<T> extends PIXI.Container {
    public static ELEMENT_CLICKED = "elementClicked";
    public static SCROLL_MOVED = "scrollMoved";
    protected static ACCELERATION_THRESHOLD = 150;
    protected static ACCELERATION_DECAY = 0.9;
    protected static MAXIMUM_SWIPE_DURATION_FOR_ACCEL = 600;
    protected static NUMBER_OF_ADDITIONAL_ELEMENTS = 2;
    protected static MIN_SCROLL_AMOUNT = 3;

    public isMoving: boolean = false;

    protected _dataArray: T[];
    protected _container: PIXI.Container;
    protected _firstElementIndex: number;

    protected _conf: {
        portrait: IScrollElementConfig;
        landscape: IScrollElementConfig;
    };

    protected _placeHolders: PIXI.Sprite[];
    protected _scrollBarTrack: PIXI.Graphics;
    protected _scrollBarThumb: PIXI.Graphics;
    protected _topFade: PIXI.Sprite
    protected _bottomFade: PIXI.Sprite

    protected _elementFactory: IScrollElementFactory<T>;

    protected _isPointerDown: boolean = false;
    protected _isPointerOver: boolean = false;
    protected _onPointerDownEventGlobalY: number;
    protected _onPointerDownContainerY: number;
    protected _pointerDownTimeStamp: number;
    protected _pointerUpTimeStamp: number;
    protected _scrollSpeed: number = 0;
    protected _hasReachedTop: boolean = true
    protected _hasReachedBottom: boolean = false
    protected _maskRectGraphic: PIXI.Graphics;
    protected _xPos: number;

    /* the maximum amount of elements that will be visible at the same time in both resolutions (including parts of elements) */
    protected _maxElementsCap: number

    constructor(elementFactory: IScrollElementFactory<T>, maxElementsCap: number, config: IScrollConfig) {
        super();
        const {
            portrait: { scrollConfig: portrait },
            landscape: { scrollConfig: landscape },
        } = config;
        this._elementFactory = elementFactory;
        if(maxElementsCap < 1) throw 'Maximum elements cap must be grater than 0.'
        this._maxElementsCap = maxElementsCap
        this._conf = { portrait, landscape };
        this.init();
    }

    
    protected init() {
        const { placeholderTextureID, fadeConfig } = this.conf;
        this.interactive = true;
        this.name = "scroll";

        this._firstElementIndex = 0;
        this._dataArray = [];

        this.createMask();
        placeholderTextureID && this.createPlaceHolders();

        this.createContainer();
        this.createElements();
        fadeConfig && this.createFade()
        this.setMask();
        this.checkIfMaskHeightIsValid();
        this.createHitArea();
        this.conf.scrollBarConfig && this.createScrollBar();
        this.setListeners();

        this._xPos = 0;

        PIXI.ticker.shared.add(this.animateScroll, this);
    }

    get orientation(): string {
        return MobileView.deviceOrientation.toString();
    }

    get conf(): IScrollElementConfig {
        return this._conf[this.orientation];
    }

    get elements(): IScrollElement<T>[] {
        return this._container.children as IScrollElement<T>[];
    }

    get numberOfElements(): number {
        return this._dataArray.length;
    }

    protected createFade(): void {
        const { fadeConfig } =this.conf
        this._topFade = new PIXI.Sprite(this.getTexture(fadeConfig.fadeTextureId))
        // this._topFade.width = this.elements[0].width
        this._topFade.scale.y *= fadeConfig.isReverse ? -1 : 1
        this.addChild(this._topFade)

        this._bottomFade = new PIXI.Sprite(this.getTexture(fadeConfig.fadeTextureId))
        this._bottomFade.scale.y *= fadeConfig.isReverse ? 1 : -1
        // this._bottomFade.width = this.elements[0].width
        this.addChild(this._bottomFade)

        this.setFadeVisibility()
        // this.positionFade()
    }

    protected positionFade(): void {
        const { fadeConfig } = this.conf
        this._topFade.position.set(0,fadeConfig.isReverse? this._topFade.height: 0)
        this._bottomFade.position.set(0,fadeConfig.isReverse? this._mask.height - this._bottomFade.height : this._mask.height)
    }

    public setInteractivity(val: boolean) {
        this._isPointerDown = false;
        this.interactive = val;
        this._scrollSpeed = 0;
        this.isMoving = false;
    }

    public setData(dataArray: T[], returnToTop?: boolean) {
        const {
            placeholderTextureID,
            scrollBarConfig,
        } = this.conf;
        if (
            dataArray.length <= this.getMaximumVisibleElements() ||
            dataArray.length < this._dataArray.length
        ) {
            returnToTop = true;
        }
        this._dataArray = [...dataArray];

        if (returnToTop) this.repositionElements(true);

        for (let i = 0; i < this.elements.length; i++) {
            this.populateElement(i, this._firstElementIndex + i);
        }

        this.reorderAndOutOfBoundsCheck();

        placeholderTextureID && this.setPlaceHolderVisibility();
        scrollBarConfig && this.setScrollbar();
    }

    public update(returnToTop: boolean = false) {
        this._scrollSpeed = 0
        this._isPointerDown = false
        this.isMoving = false
        this.setMask()
        this.updatePlaceholders();
        this.repositionElements(returnToTop);

        //TODO: should elements texture be explicitly updated here
        this.checkIfMaskHeightIsValid();
    }

    protected repositionElements(returnToTop?: boolean): void {
        const {
            elementsVerticalOffset,
            placeholderTextureID,
            scrollBarConfig,
        } = this.conf;

        let startY = this.elements[0].position.y;
        if (returnToTop) {
            this._container.y = 0;
            this._firstElementIndex = 0;
            startY = 0;
        }

        for (let i = 0; i < this._container.children.length; i++) {
            const el = this._container.getChildAt(i);
            el.y = startY + i * (el.height + elementsVerticalOffset);
        }

        this.reorderAndOutOfBoundsCheck();
        placeholderTextureID && this.setPlaceHolderVisibility();
        scrollBarConfig && this.setScrollbar();
    }

    protected updatePlaceholders() {
        this.updatePlaceholdersTexture();
        this.updatePlaceholdersPosition();
    }

    protected updatePlaceholdersTexture() {
        this._placeHolders.forEach((pl) => {
            pl.texture = this.getTexture(this.conf.placeholderTextureID);
        });
    }

    public setPosition(position: PIXI.Point) {
        const { scrollBarConfig } = this.conf;
        this._scrollSpeed = 0;
        this._isPointerDown = false;
        this.isMoving = false;
        this.position = position.clone();
        this._xPos = position.x;
        this.setMask();
        this.createHitArea();

        this.reorderAndOutOfBoundsCheck();

        scrollBarConfig && this.setScrollbar();
    }

    protected setListeners(): void {
        this.on(Device.resolveEvent("mousedown"), this.onPointerDown, this);
        this.on(Device.resolveEvent("mouseup"), this.onPointerUp, this);
        this.on(Device.resolveEvent("mouseupoutside"), this.onPointerUp, this);
        this.on(Device.resolveEvent("mouseout"), this.onPointerUp, this);
        this.on(Device.resolveEvent("mousemove"), this.onPointerMove, this);
        this.on(Device.resolveEvent("mouseover"), this.onPointerOver, this);
        this.on(Device.resolveEvent("mouseout"), this.onPointerOut, this);

        !Device.isMobile &&
            document.addEventListener(
                "wheel",
                (e: WheelEvent) => {
                    e.preventDefault();
                    this.onWheelScroll(e);
                },
                { passive: false }
            );

        this._container.children.forEach((el) => {
            el.on(Device.resolveEvent("click"), this.onElementClick, this);
        });
    }

    protected getTexture(textureID: string): PIXI.Texture {
        const loader = Loader.getLoader("gameImages") as PIXILoader;
        const texture = loader.getTexture(textureID);
        return texture;
    }

    protected onElementClick(e): void {
        if (this.isMoving) return 

        const element = e.target;
        this.emit(RecyclerScrollView.ELEMENT_CLICKED, element);
    }

    protected onPointerDown(e: InteractionEvent): void {
        if (!this.canScroll()) return;

        this._isPointerDown = true;
        this._scrollSpeed = 0;
        this._onPointerDownEventGlobalY = e.data.global.y;
        this._onPointerDownContainerY = this._container.y;

        this._pointerDownTimeStamp = null;
        this._pointerUpTimeStamp = null;
        this._pointerDownTimeStamp = Date.now();
    }

    protected onPointerUp(e): void {
        if (!this._isPointerDown || !this.canScroll()) return;

        this._pointerUpTimeStamp = Date.now();

        const pointerY = e.data.global.y;

        //reduces speed of accel based on swipe duration
        let additional = 1;
        if (this.swipeDuration() <= RecyclerScrollView.MAXIMUM_SWIPE_DURATION_FOR_ACCEL) {
            additional =
                1 -
                this.swipeDuration() / RecyclerScrollView.MAXIMUM_SWIPE_DURATION_FOR_ACCEL;
        }

        let scrolledAmount =
            (pointerY - this._onPointerDownEventGlobalY) * additional * 0.6;
        if (scrolledAmount > RecyclerScrollView.ACCELERATION_THRESHOLD)
            scrolledAmount = RecyclerScrollView.ACCELERATION_THRESHOLD;
        else if (scrolledAmount < -RecyclerScrollView.ACCELERATION_THRESHOLD)
            scrolledAmount = -RecyclerScrollView.ACCELERATION_THRESHOLD;
        this._scrollSpeed = scrolledAmount;

        this._isPointerDown = false;
        this._onPointerDownEventGlobalY = null;
        this._onPointerDownContainerY = null;
    }

    protected onPointerMove(e: InteractionEvent): void {
        const { scrollBarConfig, fadeConfig } = this.conf;
        if (!this.canScroll()) return;

        if (this._isPointerDown) {
            const pointerY = e.data.global.y;

            if (!this.isMoving) {
                if (
                    Math.abs(pointerY - this._onPointerDownEventGlobalY) >
                    RecyclerScrollView.MIN_SCROLL_AMOUNT
                ) {
                    this.emit(RecyclerScrollView.SCROLL_MOVED, this);
                    this.isMoving = true;
                }
            }

            this._hasReachedBottom = false
            this._hasReachedTop = false

            this._container.y =
                this._onPointerDownContainerY +
                pointerY -
                this._onPointerDownEventGlobalY;

            this.reorderAndOutOfBoundsCheck();

            scrollBarConfig && this.setScrollThumbPosition();
            fadeConfig && this.setFadeVisibility()
        }
    }

    protected setFadeVisibility(): void {
        this._topFade.visible = this._hasReachedBottom
        this._bottomFade.visible = this._hasReachedTop
    }

    protected onPointerOver(): void {
        this._isPointerOver = true;
    }

    protected onPointerOut(): void {
        this._isPointerOver = false;
    }

    protected onWheelScroll(e: WheelEvent): void {
        const { scrollBarConfig } = this.conf;
        if (!this._isPointerOver || !this.canScroll()) return;
        this._scrollSpeed = 0;
        this._container.y -= e.deltaY;

        this.reorderAndOutOfBoundsCheck();

        scrollBarConfig && this.setScrollThumbPosition();
    }

    protected updateScrollThumbHeight(): void {
        const { scrollBarConfig } = this.conf;
        const conf = scrollBarConfig;

        const boughtTicketsRatio =
            this.getMaximumVisibleElements() / this.numberOfElements;

        const newHeight =
            this.mask.height * boughtTicketsRatio < scrollBarConfig.minThumbSize
                ? scrollBarConfig.minThumbSize
                : this.mask.height * boughtTicketsRatio;

        // // changing the height of a rounded rect dynamically affects the radius so the rounded rect should be created again on change
        // this.removeChild(this._scrollBarThumb);
        this._scrollBarThumb.clear()
        this._scrollBarThumb.beginFill(conf.thumbFill);
        this._scrollBarThumb.drawRoundedRect(
            0,
            0,
            conf.width,
            newHeight,
            conf.radius
        );
        this._scrollBarThumb.endFill();
        // this.addChild(this._scrollBarThumb);
        this._scrollBarThumb.position.set(this.mask.width - conf.width, 0);
    }

    protected checkOutOfBounds(): void {
        const firstElement = this._container.getChildAt(0);

        let lastElement;
        if (
            this._dataArray.length <= this._container.children.length &&
            this._dataArray.length > 0
        ) {
            lastElement = this._container.getChildAt(
                this._dataArray.length - 1
            );
        } else {
            lastElement = this._container.getChildAt(
                this._container.children.length - 1
            );
        }

        const pos = this.getGlobalPosition();

        if (
            this._firstElementIndex === 0 &&
            firstElement.getGlobalPosition().y >= pos.y
        ) {
            //stick first element at the top edge of the mask
            this._scrollSpeed = 0;
            const offset = pos.y - firstElement.getGlobalPosition().y;
            this._container.y += offset;

            this._hasReachedTop = true
        } else if (
            this.getLastElementIndex() === this._dataArray.length - 1 &&
            lastElement.getGlobalPosition().y + lastElement.height <=
                pos.y + this.mask.height
        ) {
            //stick last element at the bottom edge of the mask
            this._scrollSpeed = 0;
            const offset =
                pos.y +
                this.mask.height -
                (lastElement.getGlobalPosition().y + lastElement.height);

            this._container.y += offset;

            this._hasReachedBottom = true
        }
    }

    protected checkForElementShift(): void {
        //checks if first or last element should be shifted
        const { elementsVerticalOffset } = this.conf;

        if (this._dataArray.length <= this._container.children.length) return;

        const firstChild = this._container.getChildAt(0);
        const lastChild = this._container.getChildAt(
            this._container.children.length - 1
        );

        /* global positions */
        const pos = this.getGlobalPosition();
        const firstChildGlobalY = firstChild.getGlobalPosition().y;
        const lastChildGlobalY = lastChild.getGlobalPosition().y;

        if (
            this.getLastElementIndex() < this._dataArray.length - 1 &&
            lastChildGlobalY + lastChild.height <= pos.y + this.mask.height
        ) {
            firstChild.position.y =
                lastChild.position.y +
                lastChild.height +
                elementsVerticalOffset;
            this._container.setChildIndex(
                firstChild,
                this._container.children.length - 1
            );
            this._firstElementIndex++;

            this.populateElement(
                this._container.children.length - 1,
                this.getLastElementIndex()
            );

            this.checkForElementShift();
        } else if (
            this._firstElementIndex > 0 &&
            firstChildGlobalY + firstChild.height >= pos.y
        ) {
            lastChild.position.y =
                firstChild.position.y -
                lastChild.height -
                elementsVerticalOffset;
            this._container.setChildIndex(lastChild, 0);
            this._firstElementIndex--;

            this.populateElement(0, this._firstElementIndex);

            this.checkForElementShift();
        }
    }

    protected createContainer(): void {
        this._container = new PIXI.Container();
        this._container.interactive = true;
        this.addChild(this._container);
    }

    protected createMask(): void {
        this._maskRectGraphic = new PIXI.Graphics();
        this.addChild(this._maskRectGraphic);
        this._maskRectGraphic.position.set(0, 0);
        this.mask = this._maskRectGraphic;
    }

    protected setMask(): void {
        const {
            scrollBarConfig,
            maskSize,
            fadeConfig
        } = this.conf;
        const width = scrollBarConfig
            ? maskSize.width + scrollBarConfig.rightOffset + scrollBarConfig.width
            : maskSize.width;

        this._maskRectGraphic.clear();
        this._maskRectGraphic.beginFill(0x000000, 0);
        this._maskRectGraphic.drawRect(0, 0, width, maskSize.height);

        fadeConfig && this.positionFade()
    }

    protected createHitArea(): void {
        const { maskSize } =
            this.conf;
        this.hitArea = null;
        this.hitArea = new PIXI.Rectangle(
            0,
            0,
            maskSize.width,
            maskSize.height
        );
    }

    protected createElements(): void {
        const { elementsVerticalOffset } =
            this.conf;
        for (
            let i = 0;
            i <
            this._maxElementsCap + RecyclerScrollView.NUMBER_OF_ADDITIONAL_ELEMENTS;
            i++
        ) {
            const el = this._elementFactory.instance();
            el.y = i * (el.height + elementsVerticalOffset);
            el.alpha = 0;
            this._container.addChild(el);
        }
    }

    protected createPlaceHolders(): void {
        const {
            placeholderTextureID,
            elementsVerticalOffset,
        } = this.conf;
        this._placeHolders = [];
        for (let i = 0; i < this._maxElementsCap; i++) {
            const placeholder = new PIXI.Sprite(
                this.getTexture(placeholderTextureID)
            );
            placeholder.y = i * (placeholder.height + elementsVerticalOffset);
            placeholder.visible = false;
            this._placeHolders.push(placeholder);
            this.addChild(placeholder);
        }
        this.setPlaceHolderVisibility();
        this.updatePlaceholdersPosition();
    }

    protected updatePlaceholdersPosition(): void {
        const { elementsVerticalOffset } = this.conf;
        this._placeHolders.forEach((pl, index) => {
            pl.y = index * (pl.height + elementsVerticalOffset);
        });
    }

    protected setPlaceHolderVisibility(): void {
        for (let i = 0; i < this._placeHolders.length; i++) {
            this._placeHolders[i].visible = !this._dataArray[i];
        }
    }

    protected createScrollBar(): void {
        const { scrollBarConfig } = this.conf;
        const conf = scrollBarConfig;

        this._scrollBarTrack = new PIXI.Graphics();
        this._scrollBarTrack.beginFill(conf.trackFill);
        this._scrollBarTrack.drawRoundedRect(
            0,
            0,
            conf.width,
            this.mask.height,
            conf.radius
        );
        this._scrollBarTrack.endFill();
        this.addChild(this._scrollBarTrack);
        this._scrollBarTrack.position.set(this.mask.width - conf.width, 0);

        this._scrollBarThumb = new PIXI.Graphics();
        this._scrollBarThumb.beginFill(conf.thumbFill);
        this._scrollBarThumb.drawRoundedRect(
            0,
            0,
            conf.width,
            this.mask.height,
            conf.radius
        );
        this._scrollBarThumb.endFill();
        this.addChild(this._scrollBarThumb);
        this._scrollBarThumb.position.set(this.mask.width - conf.width, 0);
    }

    protected setScrollbar(): void {
        const { scrollBarConfig } = this.conf;
        if (scrollBarConfig) {
            if (!this.canScroll()) {
                this._scrollBarThumb.visible = this._scrollBarTrack.visible =
                    false;
            } else {
                this._scrollBarThumb.visible = this._scrollBarTrack.visible =
                    true;
                this._scrollBarTrack.height = this._mask.height;
                this.updateScrollThumbHeight();
                this.setScrollThumbPosition();
            }
            this.updateScrollBarHorizontalPosition();
        }
    }

    protected setScrollThumbPosition(): void {
        let ratio;

        const { elementsVerticalOffset } = this.conf;
        const firstChild = this._container.getChildAt(0);
        const totalElementsHeight =
            this._dataArray.length *
                (firstChild.height + elementsVerticalOffset) -
            elementsVerticalOffset;
        const prevElementsHeight =
            this._firstElementIndex *
            (firstChild.height + elementsVerticalOffset);
        const containerGlobalPos =
            firstChild.getGlobalPosition().y - prevElementsHeight;
        const totalScrolledDistance =
            this.getGlobalPosition().y - containerGlobalPos;

        if (totalScrolledDistance === 0) {
            ratio = 0;
        } else {
            ratio =
                totalScrolledDistance /
                (totalElementsHeight - this.mask.height);
        }

        this._scrollBarThumb.y =
            (this._scrollBarTrack.height - this._scrollBarThumb.height) * ratio;
    }

    protected updateScrollBarHorizontalPosition(): void {
        const { scrollBarConfig } = this.conf;
        if (this._scrollBarThumb.visible) {
            this._scrollBarTrack.x = this.mask.width - scrollBarConfig.width;
            this._scrollBarThumb.x = this.mask.width - scrollBarConfig.width;
            this.x = this._xPos;
        } else {
            this.x =
                this._xPos +
                (scrollBarConfig.width + scrollBarConfig.rightOffset) / 2;
        }
    }

    protected populateElement(
        elementIndex: number,
        dataArrayIndex: number
    ): void {
        const element: IScrollElement<T> =
            this._container.getChildAt(elementIndex);
        const ticketData = this._dataArray[dataArrayIndex];
        if(ticketData) {
            element.alpha = 1
            element.interactive = true
            element.setData(ticketData);
        } else {
            element.alpha = 0
            element.interactive = false
        }
    }

    protected canScroll(): boolean {
        const trueCases = this.mask.height < this._dataArray.length*(this.elements[0].height + this.conf.elementsVerticalOffset) - this.conf.elementsVerticalOffset

        return trueCases;
    }

    protected swipeDuration(): number {
        if (!this._pointerUpTimeStamp || !this._pointerDownTimeStamp) return 0;
        return this._pointerUpTimeStamp - this._pointerDownTimeStamp;
    }

    protected getLastElementIndex() {
        if (this._dataArray.length < this._container.children.length)
            return this._dataArray.length - 1;
        return this._firstElementIndex + this._container.children.length - 1;
    }

    protected animateScroll(): void {
        const { scrollBarConfig, fadeConfig } = this.conf;
        if (!this.visible) return;

        if (!this.canScroll()) return;

        if (!this._isPointerDown && this.isMoving) {
            if (
                this.swipeDuration() > RecyclerScrollView.MAXIMUM_SWIPE_DURATION_FOR_ACCEL
            ) {
                this.isMoving = false;
                this._scrollSpeed = 0;
                this._pointerDownTimeStamp = null;
                this._pointerUpTimeStamp = null;
                scrollBarConfig && this.setScrollThumbPosition();
                return;
            }

            if (
                this._scrollSpeed === 0 ||
                (this._scrollSpeed < 1 && this._scrollSpeed > -1)
            ) {
                this.isMoving = false;
                this._scrollSpeed = 0;

                this.reorderAndOutOfBoundsCheck();

                scrollBarConfig && this.setScrollThumbPosition();
                return;
            }

            this._scrollSpeed *= RecyclerScrollView.ACCELERATION_DECAY;

            this._container.y += this._scrollSpeed;

            this.reorderAndOutOfBoundsCheck();

            scrollBarConfig && this.setScrollThumbPosition();
            fadeConfig && this.setFadeVisibility()
        }
    }

    protected reorderAndOutOfBoundsCheck(): void {
        this.checkForElementShift();
        this.checkOutOfBounds();
    }

    protected checkIfMaskHeightIsValid(): void {
        const elementHeight = this.elements[0].height
        const maskHeight = this.conf.maskSize.height
        const elVertOffset = this.conf.elementsVerticalOffset
        const totalHeightOfVisibleElements = (elementHeight + elVertOffset) * this._maxElementsCap - elVertOffset
        if(maskHeight > totalHeightOfVisibleElements){
            throw `Mask height in ${this.orientation} should be lesser than or equal to ${totalHeightOfVisibleElements} 
            or the maximum elements cap should be greater than ${this._maxElementsCap}.`
        }
    }

    protected getMaximumVisibleElements(): number {
        const {maskSize, elementsVerticalOffset} = this.conf
        return Math.floor((maskSize.height - elementsVerticalOffset) / (this.elements[0].height + elementsVerticalOffset))
    }
}
